<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-card class="search-card" shadow="never">
      <el-form
        :model="filters"
        ref="queryForm"
        size="small"
        :inline="true"
        label-width="80px"
        @submit.native.prevent
      >
        <el-form-item label="客户名称" prop="customerName">
          <el-input
            v-model="filters.customerName"
            placeholder="请输入客户名称"
            clearable
            style="width: 200px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="客户类型" prop="customerType">
          <el-select
            v-model="filters.customerType"
            placeholder="请选择客户类型"
            clearable
            style="width: 160px;"
          >
            <el-option label="公司" :value="0" />
            <el-option label="个人" :value="1" />
          </el-select>
        </el-form-item>
        <el-form-item label="订单状态" prop="orderStatus">
          <el-select
            v-model="filters.orderStatus"
            placeholder="请选择订单状态"
            clearable
            style="width: 160px;"
          >
            <el-option label="待审核" :value="0" />
            <el-option label="审核失败" :value="1" />
            <el-option label="已完成" :value="2" />
            <el-option label="已作废" :value="3" />
            <el-option label="待补差价" :value="4" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 列表区域 -->
    <el-card class="table-card" shadow="never">
      <div class="table-toolbar">
        <div class="toolbar-left">
          <router-link to="/order-form">
            <el-button type="primary" plain icon="el-icon-plus" size="mini">创建订单</el-button>
          </router-link>
        </div>
        <div class="toolbar-right">
          <el-tooltip content="刷新" placement="top">
            <el-button
              circle
              icon="el-icon-refresh"
              size="mini"
              @click="fetchOrders"
            />
          </el-tooltip>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="orders"
        border
        stripe
        size="small"
        style="width: 100%;"
        :header-cell-style="headerCellStyle"
      >
        <el-table-column prop="orderId" label="订单编号" width="100" align="center">
          <template slot-scope="scope">
            #{{ scope.row.orderId }}
          </template>
        </el-table-column>
        <el-table-column prop="customerName" label="客户名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="产品" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.productName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="总金额" width="110" align="center">
          <template slot-scope="scope">
            <span class="amount-text">¥{{ scope.row.totalAmount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="数量" width="80" align="center">
          <template slot-scope="scope">
            {{ scope.row.orderQuantity != null ? scope.row.orderQuantity : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="款项类型" width="100" align="center">
          <template slot-scope="scope">
            {{ getPaymentTypeLabel(scope.row.paymentType) }}
          </template>
        </el-table-column>
        <el-table-column label="客户类型" width="100" align="center">
          <template slot-scope="scope">
            {{ getCustomerTypeLabel(scope.row.customerType) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusTagType(scope.row.orderStatus)" size="mini">
              {{ getStatusLabel(scope.row.orderStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="备注" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.orderRemark || '无' }}
          </template>
        </el-table-column>
        <el-table-column label="创建日期" width="160" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createdTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" align="center" fixed="right" class-name="small-padding fixed-width">
          <template slot-scope="scope">
            <el-button
              v-if="canEditOrder(scope.row)"
              type="text"
              icon="el-icon-edit"
              size="mini"
              @click="openEditOrder(scope.row)"
            >修改</el-button>
            <el-button
              v-if="canCancelOrder(scope.row)"
              type="text"
              icon="el-icon-close"
              size="mini"
              class="warning-text"
              @click="handleCancel(scope.row.orderId)"
            >作废</el-button>
            <el-button
              v-if="Number(scope.row.orderStatus) === ORDER_STATUS.PENDING_BALANCE"
              type="text"
              icon="el-icon-upload2"
              size="mini"
              @click="openBalanceUpload(scope.row)"
            >上传补差价</el-button>
            <el-button
              type="text"
              icon="el-icon-view"
              size="mini"
              @click="openOrderDetail(scope.row)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap" v-if="total > 0">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- 订单详情抽屉 -->
    <el-drawer
      title="订单详情"
      :visible.sync="detailDrawerVisible"
      size="480px"
      append-to-body
      @closed="onDetailDrawerClosed"
    >
      <div v-loading="detailLoading" class="detail-drawer-body" v-if="currentDetailOrder">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="订单编号">#{{ currentDetailOrder.orderId }}</el-descriptions-item>
          <el-descriptions-item label="客户名称">{{ currentDetailOrder.customerName }}</el-descriptions-item>
          <el-descriptions-item label="产品">{{ currentDetailOrder.productName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="总金额">¥{{ currentDetailOrder.totalAmount }}</el-descriptions-item>
          <el-descriptions-item label="数量">
            {{ currentDetailOrder.orderQuantity != null ? currentDetailOrder.orderQuantity : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="款项类型">
            {{ getPaymentTypeLabel(currentDetailOrder.paymentType) }}
          </el-descriptions-item>
          <el-descriptions-item
            v-if="Number(currentDetailOrder.paymentType) === PAYMENT_TYPE.DEPOSIT"
            label="定金金额"
          >
            <span class="deposit-text">¥{{ currentDetailOrder.depositAmount || 0 }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="客户类型">
            {{ getCustomerTypeLabel(currentDetailOrder.customerType) }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(currentDetailOrder.orderStatus)" size="mini">
              {{ getStatusLabel(currentDetailOrder.orderStatus) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建日期">
            {{ formatDate(currentDetailOrder.createdTime) }}
          </el-descriptions-item>
          <el-descriptions-item label="备注">
            {{ currentDetailOrder.orderRemark || '无' }}
          </el-descriptions-item>
        </el-descriptions>

        <!-- 按截图类型展示：2全款 / 0定金 / 1补差价；旧全款单兼容 orderScreenshotUrl -->
        <div
          class="screenshots-section"
          v-if="Number(currentDetailOrder.paymentType) === PAYMENT_TYPE.FULL
            && getFullScreenshotUrls(currentDetailOrder.orderId).length"
        >
          <p class="section-title">全款截图</p>
          <div class="screenshots-grid">
            <img
              v-for="(url, index) in getFullScreenshotUrls(currentDetailOrder.orderId)"
              :key="'f' + index"
              :src="url"
              alt="全款截图"
              class="screenshot-thumb"
              @click="openImageViewer(getFullScreenshotUrls(currentDetailOrder.orderId), index, '全款截图')"
              @error="handleImageError($event, '全款截图')"
            >
          </div>
        </div>

        <div
          class="screenshots-section"
          v-if="Number(currentDetailOrder.paymentType) === PAYMENT_TYPE.DEPOSIT
            && getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.DEPOSIT).length"
        >
          <p class="section-title">定金截图</p>
          <div class="screenshots-grid">
            <img
              v-for="(url, index) in getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.DEPOSIT)"
              :key="'d' + index"
              :src="url"
              alt="定金截图"
              class="screenshot-thumb"
              @click="openImageViewer(getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.DEPOSIT), index, '定金截图')"
              @error="handleImageError($event, '定金截图')"
            >
          </div>
        </div>

        <div
          class="screenshots-section"
          v-if="Number(currentDetailOrder.paymentType) === PAYMENT_TYPE.DEPOSIT
            && getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.BALANCE).length"
        >
          <p class="section-title">补差价截图</p>
          <div class="screenshots-grid">
            <img
              v-for="(url, index) in getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.BALANCE)"
              :key="'b' + index"
              :src="url"
              alt="补差价截图"
              class="screenshot-thumb"
              @click="openImageViewer(getScreenshotsByType(currentDetailOrder.orderId, SCREENSHOT_TYPE.BALANCE), index, '补差价截图')"
              @error="handleImageError($event, '补差价截图')"
            >
          </div>
        </div>
      </div>
    </el-drawer>

    <!-- 修改订单弹窗 -->
    <el-dialog
      title="修改订单"
      :visible.sync="editDialogVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetEditDialog"
    >
      <el-form
        v-if="editForm"
        ref="editForm"
        :model="editForm"
        :rules="editRules"
        label-width="100px"
        size="small"
      >
        <el-form-item label="订单编号">
          <el-input :value="editForm.orderId" disabled />
        </el-form-item>
        <el-form-item label="客户名称" prop="customerName">
          <el-input v-model="editForm.customerName" maxlength="100" />
        </el-form-item>
        <el-form-item label="客户类型" prop="customerType">
          <el-radio-group v-model="editForm.customerType">
            <el-radio :label="CUSTOMER_TYPE.COMPANY">公司</el-radio>
            <el-radio :label="CUSTOMER_TYPE.INDIVIDUAL">个人</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="产品" prop="productId">
          <el-select v-model="editForm.productId" style="width: 100%;" @change="onEditProductChange">
            <el-option
              v-for="product in productList"
              :key="product.id"
              :label="`${product.productName}（¥${formatPrice(product.price)}）`"
              :value="product.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="数量" prop="orderQuantity">
          <el-input-number
            v-model="editForm.orderQuantity"
            :min="1"
            controls-position="right"
            style="width: 100%;"
            @change="computeEditAmount"
          />
        </el-form-item>
        <el-form-item label="总金额">
          <el-input :value="'¥' + formatPrice(editForm.totalAmount)" disabled />
        </el-form-item>
        <el-form-item label="款项类型">
          <el-tag size="mini">{{ getPaymentTypeLabel(editForm.paymentType) }}</el-tag>
          <span class="edit-lock-tip">创建后不可更改款项类型</span>
        </el-form-item>
        <el-form-item
          v-if="Number(editForm.paymentType) === PAYMENT_TYPE.DEPOSIT"
          label="定金金额"
          prop="depositAmount"
        >
          <el-input-number
            v-model="editForm.depositAmount"
            :min="0.01"
            :precision="2"
            :step="100"
            controls-position="right"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="订单备注">
          <el-input v-model="editForm.orderRemark" type="textarea" :rows="2" maxlength="500" />
        </el-form-item>
        <el-form-item :label="Number(editForm.paymentType) === PAYMENT_TYPE.DEPOSIT ? '定金截图' : '全款截图'" required>
          <div class="form-tip">可重新上传替换，不可删除留空；点击图片仅放大查看</div>
          <div v-if="editScreenshotPreview" class="edit-shot-wrap">
            <div
              class="edit-upload-preview"
              title="点击查看大图"
              @click="openImageViewer([editScreenshotPreview], 0, '付款截图')"
            >
              <img :src="editScreenshotPreview" alt="付款截图">
            </div>
            <div class="edit-shot-actions">
              <el-upload
                action="#"
                :show-file-list="false"
                :http-request="uploadEditScreenshot"
                accept="image/*"
              >
                <el-button type="primary" plain size="mini" icon="el-icon-upload2">重新上传替换</el-button>
              </el-upload>
            </div>
          </div>
          <el-upload
            v-else
            action="#"
            :show-file-list="false"
            :http-request="uploadEditScreenshot"
            accept="image/*"
            drag
            class="edit-upload"
          >
            <div class="edit-upload-empty">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将图片拖到此处，或<em>点击上传</em></div>
            </div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" size="small" :loading="editSubmitting" @click="submitEditOrder">保存修改</el-button>
        <el-button size="small" :disabled="editSubmitting" @click="editDialogVisible = false">取 消</el-button>
      </div>
    </el-dialog>

    <!-- 上传补差价弹窗：先选图预览，再确认上传 -->
    <el-dialog
      title="上传补差价截图"
      :visible.sync="balanceDialogVisible"
      width="560px"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetBalanceDialog"
    >
      <div v-if="balanceUploadOrder" class="balance-dialog-body">
        <p class="balance-order-info">
          订单：#{{ balanceUploadOrder.orderId }} / 客户：{{ balanceUploadOrder.customerName }}
        </p>

        <el-upload
          v-if="!balancePreviewUrl"
          action="#"
          :show-file-list="false"
          :auto-upload="false"
          :on-change="onBalanceFileChange"
          accept="image/*"
          drag
          class="balance-upload"
        >
          <div class="balance-upload-empty">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到此处，或<em>点击选择</em></div>
          </div>
        </el-upload>

        <div v-else class="balance-preview-wrap">
          <div class="balance-preview" title="点击查看大图" @click="previewBalanceImage">
            <img :src="balancePreviewUrl" alt="补差价预览">
          </div>
          <div class="balance-preview-actions">
            <el-button type="text" size="mini" icon="el-icon-zoom-in" @click="previewBalanceImage">查看大图</el-button>
            <el-upload
              action="#"
              :show-file-list="false"
              :auto-upload="false"
              :on-change="onBalanceFileChange"
              accept="image/*"
            >
              <el-button type="text" size="mini" icon="el-icon-refresh">重新选择</el-button>
            </el-upload>
            <el-button type="text" size="mini" icon="el-icon-delete" class="danger-text" @click="clearBalanceFile">清除</el-button>
          </div>
        </div>

        <p class="balance-tip">请先选择图片确认无误后，再点击下方「确认上传」；点击图片可放大查看</p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          size="small"
          :loading="balanceUploading"
          :disabled="!balanceFile"
          @click="confirmBalanceUpload"
        >确认上传</el-button>
        <el-button size="small" :disabled="balanceUploading" @click="balanceDialogVisible = false">取 消</el-button>
      </div>
    </el-dialog>

    <!-- 图片查看器组件 -->
    <ImageViewer
      :visible="imageViewer.visible"
      :images="imageViewer.images"
      :initial-index="imageViewer.initialIndex"
      :title="imageViewer.title"
      @close="closeImageViewer"
      @indexChange="handleImageIndexChange"
    />
  </div>
</template>

<script>
import request from '@/utils/request'
import ImageViewer from '@/components/ImageViewer.vue'
import {
  CUSTOMER_TYPE,
  PAYMENT_TYPE,
  ORDER_STATUS,
  SCREENSHOT_TYPE,
  getCustomerTypeLabel,
  getPaymentTypeLabel,
  getOrderStatusLabel,
  getOrderStatusTagType
} from '@/utils/enums'

export default {
  name: 'HistoryOrders',
  components: {
    ImageViewer
  },
  data() {
    return {
      PAYMENT_TYPE,
      ORDER_STATUS,
      SCREENSHOT_TYPE,
      CUSTOMER_TYPE,
      expandedOrderId: null,
      orders: [],
      loading: false,
      error: '',
      orderDetails: {},
      currentPage: 1,
      pageSize: 10,
      total: 0,
      filters: {
        customerName: '',
        customerType: '',
        orderStatus: ''
      },
      imageViewer: {
        visible: false,
        images: [],
        initialIndex: 0,
        title: ''
      },
      detailDrawerVisible: false,
      detailLoading: false,
      currentDetailOrder: null,
      balanceDialogVisible: false,
      balanceUploadOrder: null,
      balanceFile: null,
      balancePreviewUrl: '',
      balanceUploading: false,
      editDialogVisible: false,
      editSubmitting: false,
      editForm: null,
      editScreenshotPreview: '',
      editScreenshotUrl: '',
      editScreenshotDirty: false,
      productList: [],
      editUnitPrice: 0,
      editRules: {
        customerName: [{ required: true, message: '请输入客户名称', trigger: 'blur' }],
        customerType: [{ required: true, message: '请选择客户类型', trigger: 'change' }],
        productId: [{ required: true, message: '请选择产品', trigger: 'change' }],
        orderQuantity: [{ required: true, message: '请输入数量', trigger: 'blur' }],
        depositAmount: [{ required: true, message: '请输入定金金额', trigger: 'blur' }]
      },
      headerCellStyle: {
        background: '#f8f8f9',
        color: '#515a6e',
        fontWeight: '600'
      }
    }
  },
  created() {
    this.fetchOrders()
    this.fetchProducts()
  },
  methods: {
    formatPrice(price) {
      const num = Number(price)
      return Number.isFinite(num) ? num.toFixed(2) : '0.00'
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchOrders(page, this.pageSize)
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchOrders(this.currentPage, size)
    },

    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },

    getStatusLabel(status) {
      return getOrderStatusLabel(status)
    },

    getPaymentTypeLabel(type) {
      return getPaymentTypeLabel(type)
    },

    getCustomerTypeLabel(type) {
      return getCustomerTypeLabel(type)
    },

    getStatusTagType(status) {
      return getOrderStatusTagType(status)
    },

    canCancelOrder(order) {
      // 优先用后端计算：仅刚创建且未审核的待审核订单可作废
      if (order && typeof order.canCancel === 'boolean') {
        return order.canCancel
      }
      return Number(order.orderStatus) === ORDER_STATUS.PENDING
    },
    canEditOrder(order) {
      return this.canCancelOrder(order)
    },
    async fetchProducts() {
      try {
        const response = await request.get('/api/product/list')
        this.productList = Array.isArray(response) ? response : []
      } catch (error) {
        this.productList = []
      }
    },
    async openEditOrder(order) {
      this.editDialogVisible = true
      this.editSubmitting = false
      this.editScreenshotDirty = false
      if (!this.productList.length) {
        await this.fetchProducts()
      }
      const matched = (order.productId != null && this.productList.find(p => String(p.id) === String(order.productId)))
        || this.productList.find(p => p.productName && order.productName && p.productName === order.productName)
        || this.productList.find(p => {
          const price = Number(p.price) || 0
          const qty = Number(order.orderQuantity) || 1
          const total = Number(order.totalAmount)
          return Number.isFinite(total) && Math.abs(price * qty - total) < 0.01
        })
        || this.productList[0]
      this.editUnitPrice = matched ? Number(matched.price) || 0 : 0
      this.editForm = {
        orderId: order.orderId,
        customerName: order.customerName || '',
        customerType: order.customerType != null ? Number(order.customerType) : CUSTOMER_TYPE.COMPANY,
        productId: matched ? matched.id : '',
        orderQuantity: Number(order.orderQuantity) || 1,
        totalAmount: Number(order.totalAmount) || 0,
        paymentType: Number(order.paymentType),
        depositAmount: order.depositAmount != null ? Number(order.depositAmount) : undefined,
        orderRemark: order.orderRemark || ''
      }
      this.editScreenshotPreview = ''
      this.editScreenshotUrl = ''
      try {
        const response = await request.get(`/api/orderDetail?orderId=${order.orderId}`)
        const details = this.processOrderDetailData(response || {})
        const type = Number(order.paymentType) === PAYMENT_TYPE.DEPOSIT
          ? SCREENSHOT_TYPE.DEPOSIT
          : SCREENSHOT_TYPE.FULL
        let url = ''
        if (Array.isArray(details.screenshots)) {
          const hit = details.screenshots.find(s => Number(s.screenshotType) === type && s.screenshotUrl)
          if (hit) url = hit.screenshotUrl
        }
        if (!url && Number(order.paymentType) === PAYMENT_TYPE.FULL && details.orderScreenshotUrl) {
          url = details.orderScreenshotUrl
        }
        this.editScreenshotPreview = url
        this.editScreenshotUrl = url
      } catch (error) {
        this.$message.warning('加载原截图失败，可重新上传')
      }
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.clearValidate()
      })
    },
    onEditProductChange() {
      const selected = this.productList.find(item => String(item.id) === String(this.editForm.productId))
      this.editUnitPrice = selected ? Number(selected.price) || 0 : 0
      this.computeEditAmount()
    },
    computeEditAmount() {
      if (!this.editForm) return
      const qty = Number(this.editForm.orderQuantity) || 0
      this.editForm.totalAmount = this.editUnitPrice && qty
        ? Number((this.editUnitPrice * qty).toFixed(2))
        : 0
    },
    async uploadEditScreenshot(option) {
      const file = option.file
      if (!file) return
      if (!file.type.startsWith('image/')) {
        this.$message.error('请上传图片文件')
        return
      }
      try {
        const formData = new FormData()
        formData.append('image', file)
        const api = Number(this.editForm.paymentType) === PAYMENT_TYPE.DEPOSIT
          ? '/api/common/upload/screenshot'
          : '/api/common/upload/order'
        const url = await request.post(api, formData)
        this.editScreenshotUrl = url
        this.editScreenshotPreview = url
        this.editScreenshotDirty = true
        this.$message.success('图片上传成功，保存后生效')
      } catch (error) {
        this.$message.error((error && error.message) || '图片上传失败')
      }
    },
    resetEditDialog() {
      this.editForm = null
      this.editScreenshotPreview = ''
      this.editScreenshotUrl = ''
      this.editScreenshotDirty = false
      this.editSubmitting = false
    },
    submitEditOrder() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) return
        if (!this.editScreenshotUrl) {
          this.$message.error('请保留或重新上传付款截图，不能删除留空')
          return
        }
        if (Number(this.editForm.paymentType) === PAYMENT_TYPE.DEPOSIT) {
          if (!this.editForm.depositAmount || this.editForm.depositAmount <= 0) {
            this.$message.error('请输入有效定金金额')
            return
          }
        }
        this.editSubmitting = true
        try {
          const shotType = Number(this.editForm.paymentType) === PAYMENT_TYPE.DEPOSIT
            ? SCREENSHOT_TYPE.DEPOSIT
            : SCREENSHOT_TYPE.FULL
          const payload = {
            orderId: this.editForm.orderId,
            customerName: this.editForm.customerName.trim(),
            customerType: Number(this.editForm.customerType),
            productId: Number(this.editForm.productId),
            orderQuantity: this.editForm.orderQuantity,
            totalAmount: this.editForm.totalAmount,
            paymentType: Number(this.editForm.paymentType),
            depositAmount: Number(this.editForm.paymentType) === PAYMENT_TYPE.DEPOSIT
              ? this.editForm.depositAmount
              : null,
            orderRemark: this.editForm.orderRemark || '',
            orderDetail: {
              orderScreenshotUrl: this.editScreenshotUrl
            },
            orderScreenshots: [{
              screenshotType: shotType,
              screenshotUrl: this.editScreenshotUrl
            }]
          }
          await request.put('/api/order/edit', payload)
          this.$message.success('订单修改成功')
          this.editDialogVisible = false
          this.$delete(this.orderDetails, this.editForm.orderId)
          await this.fetchOrders()
        } catch (error) {
          this.$message.error((error && error.message) || '修改失败，请重试')
        } finally {
          this.editSubmitting = false
        }
      })
    },

    handleCancel(orderId) {
      if (confirm('确定要作废这个订单吗？')) {
        this.loading = true
        this.error = ''

        request.put(`/api/order?orderStatus=${ORDER_STATUS.CANCELLED}&orderId=${orderId}`)
          .then(() => {
            this.$message.success('订单已作废')
            this.fetchOrders()
          }).catch(error => {
            this.error = error.message || '作废订单失败'
            this.$message.error(this.error)
          }).finally(() => {
            this.loading = false
          })
      }
    },

    fetchOrders(page = this.currentPage, size = this.pageSize) {
      this.loading = true
      this.error = ''

      const requestData = { page, size }
      if (this.filters.customerName) requestData.customerName = this.filters.customerName
      if (this.filters.customerType !== '' && this.filters.customerType !== null && this.filters.customerType !== undefined) {
        requestData.customerType = Number(this.filters.customerType)
      }
      if (this.filters.orderStatus !== '' && this.filters.orderStatus !== null && this.filters.orderStatus !== undefined) {
        requestData.orderStatus = Number(this.filters.orderStatus)
      }

      request.post('/api/order/list', requestData)
        .then(data => {
          const pageData = data || {}
          this.orders = pageData.records || []
          this.total = pageData.total || 0
          if (typeof pageData.current === 'number') this.currentPage = pageData.current
          if (typeof pageData.size === 'number') this.pageSize = pageData.size
        })
        .catch(error => {
          this.orders = []
          this.error = error.message || '网络错误，获取订单数据失败'
          this.$message.error(this.error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleSearch() {
      this.currentPage = 1
      this.fetchOrders(this.currentPage, this.pageSize)
    },

    handleReset() {
      this.filters = {
        customerName: '',
        customerType: '',
        orderStatus: ''
      }
      this.currentPage = 1
      this.fetchOrders(this.currentPage, this.pageSize)
    },

    async toggleOrderDetails(orderId) {
      if (this.expandedOrderId === orderId) {
        this.expandedOrderId = null
        return
      }
      this.expandedOrderId = orderId
      try {
        if (!this.orderDetails[orderId]) {
          const response = await request.get(`/api/orderDetail?orderId=${orderId}`)
          if (response) {
            const processedData = this.processOrderDetailData(response)
            this.$set(this.orderDetails, orderId, processedData)
          } else {
            this.$message.warning('未获取到订单详情数据')
          }
        }
      } catch (error) {
        this.$message.error('获取订单详情失败，请重试')
      }
    },

    async openOrderDetail(order) {
      this.currentDetailOrder = order
      this.detailDrawerVisible = true
      this.expandedOrderId = order.orderId
      this.detailLoading = true
      try {
        if (!this.orderDetails[order.orderId]) {
          const response = await request.get(`/api/orderDetail?orderId=${order.orderId}`)
          if (response) {
            const processedData = this.processOrderDetailData(response)
            this.$set(this.orderDetails, order.orderId, processedData)
          } else {
            this.$message.warning('未获取到订单详情数据')
          }
        }
      } catch (error) {
        this.$message.error('获取订单详情失败，请重试')
      } finally {
        this.detailLoading = false
      }
    },

    onDetailDrawerClosed() {
      this.currentDetailOrder = null
      this.expandedOrderId = null
    },

    processOrderDetailData(data) {
      const detail = data.orderDetail || data
      const screenshots = Array.isArray(data.screenshots) ? data.screenshots : []
      return {
        orderScreenshotUrl: detail && detail.orderScreenshotUrl
          ? String(detail.orderScreenshotUrl).replace(/[`']/g, '').trim()
          : '',
        screenshots: screenshots.map(item => ({
          ...item,
          screenshotUrl: item.screenshotUrl ? String(item.screenshotUrl).replace(/[`']/g, '').trim() : ''
        })).filter(item => item.screenshotUrl)
      }
    },

    getScreenshotsByType(orderId, type) {
      const details = this.orderDetails[orderId]
      if (!details || !Array.isArray(details.screenshots)) return []
      return details.screenshots
        .filter(item => Number(item.screenshotType) === Number(type) && item.screenshotUrl)
        .map(item => item.screenshotUrl)
    },
    getFullScreenshotUrls(orderId) {
      const typed = this.getScreenshotsByType(orderId, SCREENSHOT_TYPE.FULL)
      if (typed.length) return typed
      const details = this.orderDetails[orderId]
      const legacy = details && details.orderScreenshotUrl
        ? String(details.orderScreenshotUrl).trim()
        : ''
      return legacy ? [legacy] : []
    },

    openBalanceUpload(order) {
      this.balanceUploadOrder = order
      this.balanceFile = null
      this.balancePreviewUrl = ''
      this.balanceDialogVisible = true
    },
    onBalanceFileChange(file) {
      const raw = file && file.raw
      if (!raw) return
      if (!raw.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }
      if (this.balancePreviewUrl) {
        URL.revokeObjectURL(this.balancePreviewUrl)
      }
      this.balanceFile = raw
      this.balancePreviewUrl = URL.createObjectURL(raw)
    },
    clearBalanceFile() {
      if (this.balancePreviewUrl) {
        URL.revokeObjectURL(this.balancePreviewUrl)
      }
      this.balanceFile = null
      this.balancePreviewUrl = ''
    },
    previewBalanceImage() {
      if (!this.balancePreviewUrl) return
      this.openImageViewer([this.balancePreviewUrl], 0, '补差价截图预览')
    },
    resetBalanceDialog() {
      this.clearBalanceFile()
      this.balanceUploadOrder = null
      this.balanceUploading = false
    },
    async confirmBalanceUpload() {
      if (!this.balanceFile || !this.balanceUploadOrder) {
        this.$message.warning('请先选择补差价截图')
        return
      }
      this.balanceUploading = true
      const order = this.balanceUploadOrder
      try {
        const formData = new FormData()
        formData.append('image', this.balanceFile)
        const url = await request.post('/api/common/upload/screenshot', formData)
        await request.post('/api/orderScreenshot', {
          orderId: order.orderId,
          screenshotType: SCREENSHOT_TYPE.BALANCE,
          screenshotUrl: url
        })
        this.$message.success('补差价截图上传成功，订单已进入待审核')
        this.balanceDialogVisible = false
        this.$delete(this.orderDetails, order.orderId)
        await this.fetchOrders()
      } catch (error) {
        this.$message.error((error && error.message) || '上传失败')
      } finally {
        this.balanceUploading = false
      }
    },

    handleImageError(event, type) {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iODAiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSI2MCIgeT0iNDAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWbvueJh+WKoOi9vTwvdGV4dD48L3N2Zz4='
      event.target.alt = `${type}加载失败`
    },

    openImageViewer(images, initialIndex = 0, title = '') {
      this.imageViewer = {
        visible: true,
        images: images || [],
        initialIndex: Math.max(0, initialIndex),
        title: title
      }
    },

    closeImageViewer() {
      this.imageViewer.visible = false
    },

    handleImageIndexChange(newIndex) {
      this.imageViewer.initialIndex = newIndex
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.search-card {
  margin-bottom: 10px;
}

.search-card >>> .el-card__body {
  padding: 18px 18px 0;
}

.table-card >>> .el-card__body {
  padding: 16px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amount-text {
  color: #f56c6c;
  font-weight: 600;
}

.deposit-text {
  color: #409eff;
  font-weight: 600;
}

.warning-text {
  color: #e6a23c !important;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.detail-drawer-body {
  padding: 0 20px 20px;
}

.screenshots-section {
  margin-top: 16px;
}

.section-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.screenshots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.screenshot-thumb {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.screenshot-thumb:hover {
  transform: scale(1.05);
}

.balance-dialog-body {
  text-align: center;
}

.balance-order-info {
  margin: 0 0 12px;
  font-size: 13px;
  color: #606266;
  text-align: left;
}

.balance-upload {
  width: 100%;
}

.balance-upload >>> .el-upload,
.balance-upload >>> .el-upload-dragger {
  width: 100%;
}

.balance-upload-empty {
  padding: 28px 0;
}

.balance-preview-wrap {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.balance-preview {
  cursor: zoom-in;
  line-height: 0;
}

.balance-preview img {
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 4px;
  background: #fff;
}

.balance-preview-actions {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.danger-text {
  color: #f56c6c !important;
}

.balance-tip {
  margin: 12px 0 0;
  font-size: 12px;
  color: #909399;
  text-align: left;
}

.dialog-footer {
  text-align: right;
}

.edit-lock-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
  line-height: 1.4;
}

.edit-upload {
  width: 100%;
}

.edit-upload >>> .el-upload,
.edit-upload >>> .el-upload-dragger {
  width: 100%;
}

.edit-upload-empty {
  padding: 24px 0;
}

.edit-shot-wrap {
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
}

.edit-upload-preview {
  cursor: zoom-in;
  line-height: 0;
}

.edit-upload-preview img {
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 4px;
  background: #fff;
}

.edit-shot-actions {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}
</style>
